import { useCallback, useEffect, useState } from 'react';
import { ampli } from '~/domain/plugins/analytics';
import type {
  WaitlistFormViewedProperties,
  WaitlistSignupSubmittedProperties,
} from '~/domain/plugins/services/ampli';
import { usePluginsReady } from '~/domain/plugins/plugins-ready';
import type {
  WaitlistSubmitOutcome,
  WaitlistTrackingProps,
} from '~/ui/waitlist';

/**
 * Amplitude events around the shared `<Waitlist />` form (US-13473). Neither is
 * a `Checkout *` event, so the checkout enrichment never stamps them and every
 * payload carries `experience_id` itself.
 */

/** Where the form was shown; the tracking plan pins `surface` to these. */
export type WaitlistSurface =
  | 'experience_empty_state'
  | 'experience_dialog'
  | 'checkout_empty_state'
  | 'checkout_prompt';

export type WaitlistTrackingContext = {
  experienceId: string;
  surface: WaitlistSurface;
};

export function waitlistFormViewedProperties({
  experienceId,
  surface,
}: WaitlistTrackingContext): WaitlistFormViewedProperties {
  return { experience_id: experienceId, surface };
}

export function waitlistSignupSubmittedProperties(
  { experienceId, surface }: WaitlistTrackingContext,
  outcome: WaitlistSubmitOutcome
): WaitlistSignupSubmittedProperties {
  return { experience_id: experienceId, surface, outcome };
}

/**
 * Holds tracking calls until the plugins are ready, as `useTrackOnce` does. The
 * inline empty state reports itself viewed at hydration, before that.
 */
export function createTrackingGate() {
  let open = false;
  let waiting: (() => void)[] = [];
  return {
    open() {
      open = true;
      const calls = waiting;
      waiting = [];
      for (const call of calls) call();
    },
    run(call: () => void) {
      if (open) call();
      else waiting.push(call);
    },
  };
}

/**
 * The `onViewed` / `onSubmitted` callbacks for one waitlist surface, so the
 * routes own the Amplitude calls and the `ui/` form stays presentational.
 */
export function useWaitlistTracking({
  experienceId,
  surface,
}: WaitlistTrackingContext): WaitlistTrackingProps {
  const pluginsReady = usePluginsReady();
  const [gate] = useState(createTrackingGate);
  useEffect(() => {
    if (pluginsReady) gate.open();
  }, [pluginsReady, gate]);

  const onViewed = useCallback(() => {
    gate.run(() => {
      ampli.waitlistFormViewed(
        waitlistFormViewedProperties({ experienceId, surface })
      );
    });
  }, [gate, experienceId, surface]);

  const onSubmitted = useCallback(
    (outcome: WaitlistSubmitOutcome) => {
      gate.run(() => {
        ampli.waitlistSignupSubmitted(
          waitlistSignupSubmittedProperties({ experienceId, surface }, outcome)
        );
      });
    },
    [gate, experienceId, surface]
  );

  return { onViewed, onSubmitted };
}
